import CloseIcon from '@mui/icons-material/Close';
import { Dialog, DialogContent, IconButton } from '@mui/material';
import type { ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { selectActiveOrganizationId } from '@/entities/session';
import { useAppSelector } from '@/shared/model';
import { Loader, Features, useFeatureFlags } from '@/shared/ui';
import { useGetSharedPresetQuery } from '../../api/shareAPI';
import { FailedToLoadPresetContent } from './FailedToLoadPresetContent';
import { InvalidPresetContent } from './InvalidPresetContent';
import { PresetAppliedContent } from './PresetAppliedContent';
import { PresetFromDifferentOrganizationContent } from './PresetFromDifferentOrganizationContent';

interface Props {
  isPresetValid: (preset: unknown) => boolean;
  applyPreset: (preset: unknown) => void;
  onPresetError: () => void;
}

export function ImportSharedPreset({
  isPresetValid,
  applyPreset,
  onPresetError,
}: Props) {
  const { isFeatureAvailable } = useFeatureFlags();
  const activeOrganizationId = useAppSelector(selectActiveOrganizationId);
  const { id } = useParams();
  const navigate = useNavigate();
  const { data, isLoading, isError, isSuccess } = useGetSharedPresetQuery(id!);

  const handleCloseDialog = () => {
    navigate('..');
  };
  const handleApplyPreset = () => {
    applyPreset(data?.snapshot);
  };

  let content: ReactNode;

  if (isError) {
    content = <FailedToLoadPresetContent onMount={onPresetError} />;
  } else if (isSuccess) {
    if (!data || !isPresetValid(data.snapshot)) {
      content = <InvalidPresetContent onMount={onPresetError} />;
    } else if (
      isFeatureAvailable(Features.multiOrganization) &&
      data.organizationId &&
      activeOrganizationId !== data.organizationId
    ) {
      content = (
        <PresetFromDifferentOrganizationContent
          applyPreset={handleApplyPreset}
          presetOrganizationId={data.organizationId}
          triggerDialogClose={handleCloseDialog}
        />
      );
    } else {
      content = <PresetAppliedContent applyPreset={handleApplyPreset} />;
    }
  }

  return (
    <Dialog open onClose={handleCloseDialog}>
      <IconButton
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          zIndex: 1,
        }}
        aria-label="close"
        onClick={handleCloseDialog}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent sx={{ position: 'relative', minHeight: 100, width: 450 }}>
        {content}
        <Loader active={isLoading} />
      </DialogContent>
    </Dialog>
  );
}
